import { auth } from "@/services";
import { practiceDataService } from "./practice-data.service";

/**
 * Lernstatistik-Service. Speichert tagesgenaue Aktivitäten in
 * `practice_data.learnStats` (ein Array-Eintrag je Kalendertag) UND im
 * localStorage als schnellen Cache.
 *
 * Felder pro Eintrag:
 *   date           YYYY-MM-DD
 *   cards_reviewed Karteikarten-Reviews
 *   quiz_correct   richtige Quiz-Antworten
 *   quiz_total     gesamt Quiz-Antworten
 */

const LS_PREFIX = "lstats_";
const today = () => new Date().toISOString().slice(0, 10);

interface DayStat {
  topicId: string;
  date: string;
  cards_reviewed: number;
  quiz_correct: number;
  quiz_total: number;
}

function lsKey(topicId: string, date: string) {
  return `${LS_PREFIX}${topicId}_${date}`;
}

function loadLocal(topicId: string, date: string): DayStat {
  try {
    const raw = localStorage.getItem(lsKey(topicId, date));
    if (raw) return JSON.parse(raw) as DayStat;
  } catch { /* ignore */ }
  return { topicId, date, cards_reviewed: 0, quiz_correct: 0, quiz_total: 0 };
}

function saveLocal(stat: DayStat): void {
  try { localStorage.setItem(lsKey(stat.topicId, stat.date), JSON.stringify(stat)); } catch { /* ignore */ }
}

async function upsertRemote(stat: DayStat): Promise<void> {
  if (!auth.isAuthenticated()) return;
  try {
    const { learnStats } = await practiceDataService.read(stat.topicId);
    const payload = {
      date: stat.date,
      cards_reviewed: stat.cards_reviewed,
      quiz_correct: stat.quiz_correct,
      quiz_total: stat.quiz_total,
    };
    const idx = learnStats.findIndex((s) => s.date === stat.date);
    const next = idx >= 0 ? learnStats.map((s, i) => (i === idx ? payload : s)) : [...learnStats, payload];
    await practiceDataService.write(stat.topicId, { learnStats: next });
  } catch { /* network error – daten bleiben in LS */ }
}

export async function recordCardReview(topicId: string, count = 1): Promise<void> {
  const d = today();
  const stat = loadLocal(topicId, d);
  stat.cards_reviewed += count;
  saveLocal(stat);
  void upsertRemote(stat);
}

export async function recordQuizResult(topicId: string, correct: number, total: number): Promise<void> {
  const d = today();
  const stat = loadLocal(topicId, d);
  stat.quiz_correct += correct;
  stat.quiz_total += total;
  saveLocal(stat);
  void upsertRemote(stat);
}

/** Alle Tage mit Aktivität (letzten 90 Tage) aus LS + optional PB. */
export function getLocalHeatmapData(days = 90): { date: string; count: number }[] {
  const result: Map<string, number> = new Map();
  const now = Date.now();
  const DAY = 86_400_000;
  for (let i = 0; i < days; i++) {
    const d = new Date(now - i * DAY).toISOString().slice(0, 10);
    // Alle Einträge für diesen Tag (alle Topics)
    let count = 0;
    for (let j = 0; j < localStorage.length; j++) {
      const k = localStorage.key(j);
      if (k?.startsWith(LS_PREFIX) && k.endsWith(`_${d}`)) {
        try {
          const s = JSON.parse(localStorage.getItem(k) ?? "{}") as DayStat;
          count += (s.cards_reviewed ?? 0) + (s.quiz_total ?? 0);
        } catch { /* ignore */ }
      }
    }
    if (count > 0) result.set(d, count);
  }
  return [...result.entries()].map(([date, count]) => ({ date, count }));
}

/** Aktuellen Streak (aufeinanderfolgende Tage mit Aktivität) berechnen. */
export function getStreak(): number {
  const DAY = 86_400_000;
  const now = Date.now();
  let streak = 0;
  for (let i = 0; i < 365; i++) {
    const d = new Date(now - i * DAY).toISOString().slice(0, 10);
    let found = false;
    for (let j = 0; j < localStorage.length; j++) {
      const k = localStorage.key(j);
      if (k?.startsWith(LS_PREFIX) && k.endsWith(`_${d}`)) {
        try {
          const s = JSON.parse(localStorage.getItem(k) ?? "{}") as DayStat;
          if ((s.cards_reviewed ?? 0) + (s.quiz_total ?? 0) > 0) { found = true; break; }
        } catch { /* ignore */ }
      }
    }
    if (!found && i > 0) break; // Lücke → Streak vorbei (heute darf leer sein → i===0)
    if (found) streak++;
  }
  return streak;
}

/** Stats für alle Themen dieser Woche zusammenfassen. */
export function getWeekSummary(): { cards: number; quizCorrect: number; quizTotal: number; activeDays: number } {
  const DAY = 86_400_000;
  const now = Date.now();
  let cards = 0, quizCorrect = 0, quizTotal = 0;
  const activeDays = new Set<string>();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now - i * DAY).toISOString().slice(0, 10);
    for (let j = 0; j < localStorage.length; j++) {
      const k = localStorage.key(j);
      if (k?.startsWith(LS_PREFIX) && k.endsWith(`_${d}`)) {
        try {
          const s = JSON.parse(localStorage.getItem(k) ?? "{}") as DayStat;
          cards += s.cards_reviewed ?? 0;
          quizCorrect += s.quiz_correct ?? 0;
          quizTotal += s.quiz_total ?? 0;
          if ((s.cards_reviewed ?? 0) + (s.quiz_total ?? 0) > 0) activeDays.add(d);
        } catch { /* ignore */ }
      }
    }
  }
  return { cards, quizCorrect, quizTotal, activeDays: activeDays.size };
}

/** Stats für ein einzelnes Thema (Quiz-Quote). */
export function getTopicStats(topicId: string, days = 30): { quizQuote: number; cardsTotal: number } {
  const DAY = 86_400_000;
  const now = Date.now();
  let cards = 0, qc = 0, qt = 0;
  for (let i = 0; i < days; i++) {
    const d = new Date(now - i * DAY).toISOString().slice(0, 10);
    const s = loadLocal(topicId, d);
    cards += s.cards_reviewed;
    qc += s.quiz_correct;
    qt += s.quiz_total;
  }
  return { quizQuote: qt > 0 ? Math.round((qc / qt) * 100) : -1, cardsTotal: cards };
}
